import type { FeatureCategory, FeatureComplexity } from './types'

export interface PublicShortcutEntry {
  id: string
  category: FeatureCategory
  complexity: FeatureComplexity
  shortcut: string
  label: string
  description?: string
}

// Client-safe shortcut reference for Learn pages.
export const PUBLIC_SHORTCUTS = [
  {
    id: 'placement-text',
    category: 'placement',
    complexity: 'beginner',
    shortcut: 'T',
    label: 'Place text annotation',
    description: 'Place a text annotation on the canvas.',
  },
  {
    id: 'placement-image',
    category: 'placement',
    complexity: 'beginner',
    shortcut: 'I',
    label: 'Place image',
  },
  {
    id: 'placement-video',
    category: 'placement',
    complexity: 'beginner',
    shortcut: 'V',
    label: 'Place video shot',
    description: 'Create a new shot on the canvas.',
  },
  {
    id: 'placement-audio',
    category: 'placement',
    complexity: 'beginner',
    shortcut: 'A',
    label: 'Place audio',
  },
  {
    id: 'placement-bin',
    category: 'placement',
    complexity: 'beginner',
    shortcut: 'B',
    label: 'Place bin',
    description: 'Create a bin for related shots.',
  },
  {
    id: 'placement-set',
    category: 'placement',
    complexity: 'intermediate',
    shortcut: 'Shift+S',
    label: 'Place set',
  },
  {
    id: 'placement-variable',
    category: 'placement',
    complexity: 'intermediate',
    shortcut: 'Shift+V',
    label: 'Place variable',
    description: 'Create a variable for reusable prompt fragments.',
  },
  {
    id: 'comment-mode',
    category: 'annotation',
    complexity: 'beginner',
    shortcut: 'C',
    label: 'Comment',
    description: 'Enter comment mode, then click the canvas or a moment on the timeline to leave a comment.',
  },
  {
    id: 'selection-select-all',
    category: 'selection',
    complexity: 'beginner',
    shortcut: 'Cmd+A',
    label: 'Select all',
  },
  {
    id: 'selection-escape',
    category: 'selection',
    complexity: 'beginner',
    shortcut: 'Escape',
    label: 'Deselect all',
  },
  {
    id: 'selection-delete',
    category: 'delete',
    complexity: 'beginner',
    shortcut: 'Delete',
    label: 'Delete selected',
  },
  {
    id: 'rating-reject',
    category: 'rating',
    complexity: 'beginner',
    shortcut: 'R',
    label: 'Toggle reject',
  },
  {
    id: 'rating-favorite',
    category: 'rating',
    complexity: 'beginner',
    shortcut: 'F',
    label: 'Toggle favorite',
  },
  {
    id: 'rating-upscale',
    category: 'rating',
    complexity: 'intermediate',
    shortcut: 'U',
    label: 'Upscale & Enhance shot',
  },
  {
    id: 'rating-star-score',
    category: 'rating',
    complexity: 'beginner',
    shortcut: '1-5',
    label: 'Set star rating',
    description: 'Set or clear an independent 1-5 star rating.',
  },
  {
    id: 'rating-color-labels',
    category: 'rating',
    complexity: 'beginner',
    shortcut: '6-0',
    label: 'Apply color label',
    description: 'Apply or clear a color label for visual organization.',
  },
  {
    id: 'rating-filter-overlay',
    category: 'rating',
    complexity: 'intermediate',
    shortcut: 'Shift+F',
    label: 'Toggle filter overlay',
  },
  {
    id: 'camera-fit-all',
    category: 'camera',
    complexity: 'beginner',
    shortcut: 'Shift+1',
    label: 'Fit all in view',
    description: 'Fit all canvas objects into view.',
  },
  {
    id: 'camera-zoom-to-selection',
    category: 'camera',
    complexity: 'intermediate',
    shortcut: 'Shift+2 / Shift+Z',
    label: 'Zoom to selection',
  },
  {
    id: 'camera-center-selection',
    category: 'camera',
    complexity: 'beginner',
    shortcut: '.',
    label: 'Center on selection',
  },
  {
    id: 'camera-zoom-in',
    category: 'camera',
    complexity: 'beginner',
    shortcut: '+',
    label: 'Zoom in',
  },
  {
    id: 'camera-zoom-out',
    category: 'camera',
    complexity: 'beginner',
    shortcut: '-',
    label: 'Zoom out',
  },
  {
    id: 'camera-viewport-reset',
    category: 'camera',
    complexity: 'beginner',
    shortcut: 'Shift+0',
    label: 'Reset zoom to 100%',
  },
  {
    id: 'clipboard-copy',
    category: 'clipboard',
    complexity: 'beginner',
    shortcut: 'Cmd+C',
    label: 'Copy',
  },
  {
    id: 'clipboard-cut',
    category: 'clipboard',
    complexity: 'beginner',
    shortcut: 'Cmd+X',
    label: 'Cut',
  },
  {
    id: 'clipboard-paste',
    category: 'clipboard',
    complexity: 'beginner',
    shortcut: 'Cmd+V',
    label: 'Paste',
  },
  {
    id: 'duplicate-selection',
    category: 'duplicate',
    complexity: 'beginner',
    shortcut: 'Cmd+D',
    label: 'Duplicate',
    description: 'Duplicate the selected canvas objects in place.',
  },
  {
    id: 'duplicate-reuse-settings',
    category: 'duplicate',
    complexity: 'advanced',
    shortcut: 'Cmd+Shift+D',
    label: 'Duplicate with settings',
  },
  {
    id: 'playback-toggle',
    category: 'playback',
    complexity: 'beginner',
    shortcut: 'Space',
    label: 'Play / pause',
    description: 'Play or pause the selected shot.',
  },
  {
    id: 'playback-scrub',
    category: 'playback',
    complexity: 'intermediate',
    shortcut: 'J/K/L',
    label: 'Scrub backward / stop / forward',
    description: 'Scrub through a shot using professional editor controls.',
  },
  {
    id: 'playback-seek-bounds',
    category: 'playback',
    complexity: 'intermediate',
    shortcut: 'Home/End',
    label: 'Jump to start / end',
  },
  {
    id: 'playback-frame-step',
    category: 'playback',
    complexity: 'intermediate',
    shortcut: 'ArrowLeft/ArrowRight',
    label: 'Step one frame',
  },
  {
    id: 'undo-redo',
    category: 'undo-redo',
    complexity: 'beginner',
    shortcut: 'Cmd+Z / Cmd+Shift+Z',
    label: 'Undo / redo',
  },
  {
    id: 'stack-lifecycle',
    category: 'stack',
    complexity: 'intermediate',
    shortcut: 'Alt+ArrowLeft/Alt+ArrowRight',
    label: 'Cycle through stack',
    description: 'Hold Alt and press the arrow keys to cycle stacked shots and compare takes.',
  },
  {
    id: 'inline-rename',
    category: 'rename',
    complexity: 'intermediate',
    shortcut: 'Enter/Escape',
    label: 'Rename object',
    description: 'Rename the selected canvas object inline.',
  },
  {
    id: 'set-viewer-navigation',
    category: 'set-viewer',
    complexity: 'advanced',
    shortcut: 'WASD/QE/Space',
    label: 'Navigate set viewer',
  },
] as const satisfies readonly PublicShortcutEntry[]

export type PublicShortcutId = (typeof PUBLIC_SHORTCUTS)[number]['id']

export type PublicShortcutLocale = 'en' | 'ja' | 'es' | 'ar' | 'ko'

export interface LocalizedPublicShortcutText {
  label: string
  description?: string
}

const PUBLIC_SHORTCUT_TEXT_JA = {
  'comment-mode': {
    label: 'コメント',
  },
  'placement-text': {
    label: 'テキスト注釈を配置',
    description: 'キャンバスにテキスト注釈を配置します。',
  },
  'placement-image': {
    label: '画像を配置',
  },
  'placement-video': {
    label: '動画ショットを配置',
    description: 'キャンバスに新しいショットを作成します。',
  },
  'placement-audio': {
    label: '音声を配置',
  },
  'placement-bin': {
    label: 'ビンを配置',
    description: '関連するショット用のビンを作成します。',
  },
  'placement-set': {
    label: 'セットを配置',
  },
  'placement-variable': {
    label: '変数を配置',
    description: '再利用できるプロンプト断片の変数を作成します。',
  },
  'selection-select-all': {
    label: 'すべて選択',
  },
  'selection-escape': {
    label: 'すべて選択解除',
  },
  'selection-delete': {
    label: '選択項目を削除',
  },
  'rating-reject': {
    label: 'リジェクトを切り替え',
  },
  'rating-favorite': {
    label: 'お気に入りを切り替え',
  },
  'rating-upscale': {
    label: 'ショットをアップスケール',
  },
  'rating-star-score': {
    label: '星評価を設定',
  },
  'rating-color-labels': {
    label: 'カラーレーベルを適用',
    description: '視覚的に整理するためのカラーレーベルを適用します。',
  },
  'rating-filter-overlay': {
    label: 'フィルター表示を切り替え',
  },
  'camera-fit-all': {
    label: 'すべてを表示範囲に合わせる',
    description: 'すべてのキャンバスオブジェクトが表示されるようにします。',
  },
  'camera-zoom-to-selection': {
    label: '選択範囲にズーム',
  },
  'camera-center-selection': {
    label: '選択範囲を中央に表示',
  },
  'camera-zoom-in': {
    label: 'ズームイン',
  },
  'camera-zoom-out': {
    label: 'ズームアウト',
  },
  'camera-viewport-reset': {
    label: 'ズームを100%に戻す',
  },
  'clipboard-copy': {
    label: 'コピー',
  },
  'clipboard-cut': {
    label: 'カット',
  },
  'clipboard-paste': {
    label: 'ペースト',
  },
  'duplicate-selection': {
    label: '複製',
    description: '選択したキャンバスオブジェクトをその場で複製します。',
  },
  'duplicate-reuse-settings': {
    label: '設定付きで複製',
  },
  'playback-toggle': {
    label: '再生 / 一時停止',
    description: '選択したショットを再生または一時停止します。',
  },
  'playback-scrub': {
    label: '逆再生 / 停止 / 再生でスクラブ',
    description: 'プロ編集ソフトと同じ操作でショットをスクラブします。',
  },
  'playback-seek-bounds': {
    label: '先頭 / 末尾へ移動',
  },
  'playback-frame-step': {
    label: '1フレーム移動',
  },
  'undo-redo': {
    label: '取り消し / やり直し',
  },
  'stack-lifecycle': {
    label: 'スタック内を切り替え',
    description: 'スタックされたショットを切り替えてテイクを比較します。',
  },
  'inline-rename': {
    label: 'オブジェクト名を変更',
    description: '選択したキャンバスオブジェクトの名前をインラインで変更します。',
  },
  'set-viewer-navigation': {
    label: 'セットビューアーを移動',
  },
} satisfies Record<PublicShortcutId, LocalizedPublicShortcutText>

const PUBLIC_SHORTCUT_TEXT_ES = {
  'comment-mode': {
    label: 'Comentar',
  },
  'placement-text': {
    label: 'Colocar anotación de texto',
    description: 'Coloca una anotación de texto en el lienzo.',
  },
  'placement-image': {
    label: 'Colocar imagen',
  },
  'placement-video': {
    label: 'Colocar toma de video',
    description: 'Crea una nueva toma en el lienzo.',
  },
  'placement-audio': {
    label: 'Colocar audio',
  },
  'placement-bin': {
    label: 'Colocar bin',
    description: 'Crea un bin para agrupar tomas relacionadas.',
  },
  'placement-set': {
    label: 'Colocar set',
  },
  'placement-variable': {
    label: 'Colocar variable',
    description: 'Crea una variable para fragmentos de prompt reutilizables.',
  },
  'selection-select-all': {
    label: 'Seleccionar todo',
  },
  'selection-escape': {
    label: 'Limpiar selección',
  },
  'selection-delete': {
    label: 'Eliminar selección',
  },
  'rating-reject': {
    label: 'Alternar descarte',
  },
  'rating-favorite': {
    label: 'Alternar favorito',
  },
  'rating-upscale': {
    label: 'Hacer upscale de la toma',
  },
  'rating-star-score': {
    label: 'Establecer calificación',
  },
  'rating-color-labels': {
    label: 'Aplicar etiqueta de color',
    description: 'Aplica una etiqueta de color para organizar visualmente.',
  },
  'rating-filter-overlay': {
    label: 'Alternar overlay de filtros',
  },
  'camera-fit-all': {
    label: 'Encajar todo a la vista',
    description: 'Encaja todos los objetos del lienzo en la vista.',
  },
  'camera-zoom-to-selection': {
    label: 'Zoom a la selección',
  },
  'camera-center-selection': {
    label: 'Centrar la selección',
  },
  'camera-zoom-in': {
    label: 'Acercar',
  },
  'camera-zoom-out': {
    label: 'Alejar',
  },
  'camera-viewport-reset': {
    label: 'Restablecer zoom al 100%',
  },
  'clipboard-copy': {
    label: 'Copiar',
  },
  'clipboard-cut': {
    label: 'Cortar',
  },
  'clipboard-paste': {
    label: 'Pegar',
  },
  'duplicate-selection': {
    label: 'Duplicar',
    description: 'Duplica los objetos del lienzo seleccionados en su lugar.',
  },
  'duplicate-reuse-settings': {
    label: 'Duplicar con ajustes',
  },
  'playback-toggle': {
    label: 'Reproducir / pausar',
    description: 'Reproduce o pausa la toma seleccionada.',
  },
  'playback-scrub': {
    label: 'Scrub atrás / detener / adelante',
    description: 'Haz scrub en una toma con los mismos controles que el software profesional de edición.',
  },
  'playback-seek-bounds': {
    label: 'Ir al inicio / al final',
  },
  'playback-frame-step': {
    label: 'Avanzar un frame',
  },
  'undo-redo': {
    label: 'Deshacer / rehacer',
  },
  'stack-lifecycle': {
    label: 'Recorrer la pila',
    description: 'Alterna entre las tomas apiladas para comparar takes.',
  },
  'inline-rename': {
    label: 'Renombrar el objeto',
    description: 'Renombra el objeto del lienzo seleccionado en línea.',
  },
  'set-viewer-navigation': {
    label: 'Navegar por el visor de sets',
  },
} satisfies Record<PublicShortcutId, LocalizedPublicShortcutText>

const PUBLIC_SHORTCUT_TEXT_AR = {
  'comment-mode': {
    label: 'تعليق',
  },
  'placement-text': {
    label: 'وضع تعليق نصي',
    description: 'ضع تعليقًا نصيًا على الكانفاس.',
  },
  'placement-image': {
    label: 'وضع صورة',
  },
  'placement-video': {
    label: 'وضع لقطة فيديو',
    description: 'أنشئ لقطة جديدة على الكانفاس.',
  },
  'placement-audio': {
    label: 'وضع صوت',
  },
  'placement-bin': {
    label: 'وضع حاوية',
    description: 'أنشئ حاوية للقطات ذات الصلة.',
  },
  'placement-set': {
    label: 'وضع مجموعة',
  },
  'placement-variable': {
    label: 'وضع متغير',
    description: 'أنشئ متغيرًا لأجزاء المطالبات القابلة لإعادة الاستخدام.',
  },
  'selection-select-all': {
    label: 'تحديد الكل',
  },
  'selection-escape': {
    label: 'إلغاء كل التحديدات',
  },
  'selection-delete': {
    label: 'حذف المحدد',
  },
  'rating-reject': {
    label: 'تبديل الرفض',
  },
  'rating-favorite': {
    label: 'تبديل المفضلة',
  },
  'rating-upscale': {
    label: 'رفع دقة اللقطة',
  },
  'rating-star-score': {
    label: 'تعيين تقييم النجوم',
  },
  'rating-color-labels': {
    label: 'تطبيق تسمية لون',
    description: 'طبّق تسمية لون للتنظيم البصري.',
  },
  'rating-filter-overlay': {
    label: 'تبديل طبقة الفلاتر',
  },
  'camera-fit-all': {
    label: 'ملاءمة الكل في العرض',
    description: 'اجعل كل كائنات الكانفاس ظاهرة داخل العرض.',
  },
  'camera-zoom-to-selection': {
    label: 'تكبير إلى التحديد',
  },
  'camera-center-selection': {
    label: 'توسيط التحديد',
  },
  'camera-zoom-in': {
    label: 'تكبير',
  },
  'camera-zoom-out': {
    label: 'تصغير',
  },
  'camera-viewport-reset': {
    label: 'إعادة التكبير إلى 100%',
  },
  'clipboard-copy': {
    label: 'نسخ',
  },
  'clipboard-cut': {
    label: 'قص',
  },
  'clipboard-paste': {
    label: 'لصق',
  },
  'duplicate-selection': {
    label: 'تكرار',
    description: 'كرّر كائنات الكانفاس المحددة في مكانها.',
  },
  'duplicate-reuse-settings': {
    label: 'تكرار مع الإعدادات',
  },
  'playback-toggle': {
    label: 'تشغيل / إيقاف مؤقت',
    description: 'شغّل اللقطة المحددة أو أوقفها مؤقتًا.',
  },
  'playback-scrub': {
    label: 'ترجيع / إيقاف / تقديم',
    description: 'تنقّل داخل اللقطة باستخدام عناصر تحكم التحرير الاحترافية.',
  },
  'playback-seek-bounds': {
    label: 'القفز إلى البداية / النهاية',
  },
  'playback-frame-step': {
    label: 'التقدم إطارًا واحدًا',
  },
  'undo-redo': {
    label: 'تراجع / إعادة',
  },
  'stack-lifecycle': {
    label: 'التنقل داخل المكدس',
    description: 'تنقّل بين اللقطات المكدسة وقارن بين النسخ.',
  },
  'inline-rename': {
    label: 'إعادة تسمية الكائن',
    description: 'أعد تسمية كائن الكانفاس المحدد مباشرة.',
  },
  'set-viewer-navigation': {
    label: 'التنقل في عارض المجموعة',
  },
} satisfies Record<PublicShortcutId, LocalizedPublicShortcutText>

const PUBLIC_SHORTCUT_TEXT_KO = {
  'comment-mode': {
    label: '댓글',
  },
  'placement-text': {
    label: '텍스트 주석 배치',
    description: '캔버스에 텍스트 주석을 배치합니다.',
  },
  'placement-image': {
    label: '이미지 배치',
  },
  'placement-video': {
    label: '비디오 샷 배치',
    description: '캔버스에 새 샷을 만듭니다.',
  },
  'placement-audio': {
    label: '오디오 배치',
  },
  'placement-bin': {
    label: '빈 배치',
    description: '관련 샷을 묶을 빈을 만듭니다.',
  },
  'placement-set': {
    label: '세트 배치',
  },
  'placement-variable': {
    label: '변수 배치',
    description: '재사용 가능한 프롬프트 조각을 위한 변수를 만듭니다.',
  },
  'selection-select-all': {
    label: '전체 선택',
  },
  'selection-escape': {
    label: '전체 선택 해제',
  },
  'selection-delete': {
    label: '선택 항목 삭제',
  },
  'rating-reject': {
    label: '리젝트 전환',
  },
  'rating-favorite': {
    label: '즐겨찾기 전환',
  },
  'rating-upscale': {
    label: '샷 업스케일',
  },
  'rating-star-score': {
    label: '별점 설정',
  },
  'rating-color-labels': {
    label: '색상 라벨 적용',
    description: '시각적 정리를 위해 색상 라벨을 적용합니다.',
  },
  'rating-filter-overlay': {
    label: '필터 오버레이 전환',
  },
  'camera-fit-all': {
    label: '전체를 화면에 맞추기',
    description: '모든 캔버스 객체가 보이도록 맞춥니다.',
  },
  'camera-zoom-to-selection': {
    label: '선택 항목으로 줌',
  },
  'camera-center-selection': {
    label: '선택 항목 가운데로 이동',
  },
  'camera-zoom-in': {
    label: '확대',
  },
  'camera-zoom-out': {
    label: '축소',
  },
  'camera-viewport-reset': {
    label: '줌을 100%로 재설정',
  },
  'clipboard-copy': {
    label: '복사',
  },
  'clipboard-cut': {
    label: '잘라내기',
  },
  'clipboard-paste': {
    label: '붙여넣기',
  },
  'duplicate-selection': {
    label: '복제',
    description: '선택한 캔버스 객체를 그 자리에서 복제합니다.',
  },
  'duplicate-reuse-settings': {
    label: '설정과 함께 복제',
  },
  'playback-toggle': {
    label: '재생 / 일시 정지',
    description: '선택한 샷을 재생하거나 일시 정지합니다.',
  },
  'playback-scrub': {
    label: '뒤로 / 정지 / 앞으로 스크럽',
    description: '전문 편집기 컨트롤로 샷을 스크럽합니다.',
  },
  'playback-seek-bounds': {
    label: '시작 / 끝으로 이동',
  },
  'playback-frame-step': {
    label: '한 프레임 이동',
  },
  'undo-redo': {
    label: '실행 취소 / 다시 실행',
  },
  'stack-lifecycle': {
    label: '스택 순환',
    description: '스택된 샷을 오가며 테이크를 비교합니다.',
  },
  'inline-rename': {
    label: '객체 이름 변경',
    description: '선택한 캔버스 객체의 이름을 인라인으로 바꿉니다.',
  },
  'set-viewer-navigation': {
    label: '세트 뷰어 탐색',
  },
} satisfies Record<PublicShortcutId, LocalizedPublicShortcutText>

export function getLocalizedPublicShortcutText(
  shortcut: PublicShortcutEntry,
  locale: PublicShortcutLocale,
): LocalizedPublicShortcutText {
  const map =
    locale === 'ja'
      ? PUBLIC_SHORTCUT_TEXT_JA
      : locale === 'es'
        ? PUBLIC_SHORTCUT_TEXT_ES
        : locale === 'ar'
          ? PUBLIC_SHORTCUT_TEXT_AR
          : locale === 'ko'
            ? PUBLIC_SHORTCUT_TEXT_KO
            : null
  if (map) {
    const localized: LocalizedPublicShortcutText | undefined = map[shortcut.id as PublicShortcutId]
    if (localized) return { label: localized.label, description: localized.description ?? shortcut.description }
  }

  return {
    label: shortcut.label,
    description: shortcut.description,
  }
}
